import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { HdBodygraphCenter } from '../bodygraph/hd-bodygraph.models';
import {
  HD_CENTER_NOT_SELF,
  HD_NOT_SELF_FOCUS,
  getNotSelfGateCount,
  getNotSelfImpactRank,
} from '../bodygraph/hd-center-not-self-metadata';
import { HdGateInfoService } from '../bodygraph/hd-gate-info.service';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../bodygraph/hd-content-language.service';

/** One ordered fragment of a `**bold**`-annotated string. */
export interface HdConditioningBoldSegment {
  readonly text: string;
  readonly bold: boolean;
}

/**
 * Splits a `**bold**`-annotated string (the underlined body parts from the Figma
 * cheat sheet) into ordered segments so the template can render them without
 * `innerHTML` — keeping the emphasised body parts inside real Angular nodes.
 */
export function parseHdConditioningBoldSegments(text: string): readonly HdConditioningBoldSegment[] {
  const segments: HdConditioningBoldSegment[] = [];
  const regex = /\*\*(.+?)\*\*/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ text: text.slice(lastIndex, match.index), bold: false });
    }
    segments.push({ text: match[1], bold: true });
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) {
    segments.push({ text: text.slice(lastIndex), bold: false });
  }
  return segments;
}

/**
 * Rijk "niet-zelf conditionering" center-blok — spiegelt de Figma cheat-sheet
 * kaart ("HUMAN DESIGN NIET-ZELF CONDITIONERING", node 1138:783). Hergebruikt
 * de bestaande niet-zelf deck-data (kleur, impact-rang, %, poorten, focus-badge
 * en de kleurgroep-superscript uit `HD_CENTER_NOT_SELF`) en voegt de cheat-sheet-
 * specifieke tekst (keynote, "Open"-vraag, fysiek) toe uit de
 * `human-design/conditioning`-scope. Wordt gedeeld door de cheat-sheet-pagina en
 * het conditionering-info-paneel.
 *
 * Achtergrondkleur komt rechtstreeks uit `HD_CENTER_NOT_SELF[center].cardColor`
 * (Figma hex) — géén design-token, want de kleuren zijn per kaart uniek.
 */
@Component({
  selector: 'hd-conditioning-card',
  templateUrl: './hd-conditioning-card.component.html',
  styleUrl: './hd-conditioning-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdConditioningCardComponent {
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);

  readonly center = input.required<HdBodygraphCenter>();

  protected readonly lang = computed(() => this.contentLang.effectiveLang(), HD_LANG_RECOMPUTE_ON_LOAD);
  protected readonly info = computed(() => HD_CENTER_NOT_SELF[this.center()]);
  protected readonly focusConfig = computed(() => HD_NOT_SELF_FOCUS[this.info().focus]);
  protected readonly rank = computed(() => getNotSelfImpactRank(this.center()));
  protected readonly gateCount = computed(() => getNotSelfGateCount(this.center()));

  protected readonly centerLabel = computed(() => this.gateInfo.centerLabelIn(this.center(), this.lang()));
  protected readonly impactRankLabel = computed(() => this.gateInfo.notSelfCardLabelIn('impactRank', this.lang()));
  protected readonly undefinedLabel = computed(() => this.gateInfo.notSelfCardLabelIn('undefined', this.lang()));
  protected readonly gatesLabel = computed(() => this.gateInfo.notSelfCardLabelIn('gates', this.lang()));
  protected readonly focusKeynote = computed(() => this.gateInfo.notSelfFocusKeynoteIn(this.info().focus, this.lang()));

  protected readonly keynote = computed(() => this.text(`centers.${this.center()}.keynote`));
  protected readonly openQuestion = computed(() => this.text(`centers.${this.center()}.open`));
  protected readonly challenges = computed(() => this.text(`centers.${this.center()}.challenges`));
  protected readonly bodyPartSegments = computed(() =>
    parseHdConditioningBoldSegments(this.text(`centers.${this.center()}.bodyParts`)),
  );

  protected readonly openLabel = computed(() => this.text('labels.open'));
  protected readonly physicalLabel = computed(() => this.text('labels.physical'));

  constructor() {
    // Beide content-talen eager laden zodat de taalwissel én de NL-fallback
    // meteen correcte tekst opleveren; `loadScopesForLang` is idempotent per taal.
    this.gateInfo.loadScopesForLang('nl');
    this.gateInfo.loadScopesForLang('en');
  }

  /** Conditioning-scope tekst met NL-fallback voor (nog) lege EN-velden. */
  private text(key: string): string {
    const lang = this.lang();
    const value = this.gateInfo.cheatSheetTextIn('conditioning', key, lang);
    if (value !== '' || lang === 'nl') {
      return value;
    }
    return this.gateInfo.cheatSheetTextIn('conditioning', key, 'nl');
  }
}
